// Frame 19 — plan & billing (33:2399). US-140.
//
// PRD 17 §3: `license_state` computes expiry and render deliberately fails
// open, so an expired customer is never told — they sit in a state no
// surface reports. This screen is the first one that reports it, which is
// why `resolved_state` is surfaced unconditionally in its own STATUS card
// rather than folded quietly into the page description.
//
// SPEC NOTE on the Figma frame, kept here rather than silently "fixed": the
// drawn frame includes a "MANAGE IN [payment provider]" button, a
// "NEXT CHARGE $149.00 on 12 Sep 2026" row, a pageviews-used meter (187,400
// of 250,000), and a footer reading "Invoices, receipts, payment methods and
// tax details are managed [by that provider]." None of that data exists in
// this run: the payment provider is excluded entirely (PRD 18 §Build
// intent), GET /licenses/usage has no pageview counter, and CLAUDE.md
// §Design file forbids drawing invoices, receipts, payment methods or tax
// IDs outright. All four are omitted here, not approximated — reported as a
// conflict rather than guessed at.

import { useState } from "react";
import { PageHeader } from "../../ui/PageHeader";
import { Card } from "../../ui/Card";
import { Badge, type BadgeTone } from "../../ui/Badge";
import { StatTile, StatRow } from "../../ui/StatTile";
import { MeterBar } from "../../ui/MeterBar";
import { ErrorState } from "../../ui/ErrorState";
import { Skeleton, SkeletonStack } from "../../ui/Skeleton";
import { Toast } from "../../ui/Toast";
import { hrefs } from "../../shell/hrefs";
import {
  useLicense,
  useLicenseUsage,
  isUnauthorized,
  type LicenseRecord,
  type ResolvedLicenseState,
} from "./api";
import { PLAN_INFO, UPGRADE_TIERS } from "./pricing";
import { formatDate, formatUsd } from "./format";
import { PlanCard } from "./PlanCard";
import "./commerce.css";

const STATE_TONE: Record<ResolvedLicenseState, BadgeTone> = {
  active: "nominal",
  trial: "flagged",
  expired: "danger",
  unknown: "neutral",
};

const STATE_LABEL: Record<ResolvedLicenseState, string> = {
  active: "active",
  trial: "trial",
  expired: "expired",
  unknown: "no license on file",
};

function describePlan(data: LicenseRecord): string {
  if (!data.plan) return "No license on file for this workspace yet.";
  const label = PLAN_INFO[data.plan].label;
  if (data.resolved_state === "expired") {
    return `You were on ${label} — this license has expired.`;
  }
  if (data.resolved_state === "trial") {
    return `You're on a ${label} trial.`;
  }
  return `You're on ${label}.`;
}

export function PlanBillingScreen({ orgSlug }: { orgSlug: string }) {
  const license = useLicense();
  const usage = useLicenseUsage();
  const [inertMessage, setInertMessage] = useState<string | null>(null);
  const to = hrefs(orgSlug);

  if (license.isPending || usage.isPending) {
    return (
      <div className="maisight-commerce-body">
        <SkeletonStack label="Loading plan and billing">
          <Skeleton shape="block" height={56} />
          <Skeleton shape="block" height={160} />
          <Skeleton shape="block" height={184} />
        </SkeletonStack>
      </div>
    );
  }

  if (license.isError) {
    return (
      <div className="maisight-commerce-body">
        <ErrorState
          headline={
            isUnauthorized(license.error)
              ? "Sign in to view billing"
              : "Could not load your plan"
          }
          detail={license.error.message}
        />
      </div>
    );
  }

  const data = license.data;
  const state = data.resolved_state;
  const domainLimit = data.plan ? PLAN_INFO[data.plan].domainLimit : null;
  // usage.isPending gated the loading branch above, but usage can still be
  // in an error state independently of license — never crash rendering it.
  const usageData = usage.isError ? null : usage.data;

  return (
    <div className="maisight-commerce-body">
      <PageHeader
        eyebrow="BILLING"
        title="Plan & billing"
        description={describePlan(data)}
      />

      <Card header="STATUS">
        <div className="maisight-commerce-status-row">
          <Badge tone={STATE_TONE[state]} lamp>
            {STATE_LABEL[state]}
          </Badge>
          {state === "trial" ? (
            <span className="type-bodyS maisight-commerce-status-expiry">
              {data.days_remaining !== null
                ? `${data.days_remaining} ${data.days_remaining === 1 ? "day" : "days"} remaining`
                : "expiry unknown"}
              {data.expires_at ? ` · ends ${formatDate(data.expires_at)}` : ""}
            </span>
          ) : null}
          {state === "expired" ? (
            <span className="type-bodyS maisight-commerce-status-expiry">
              {data.expires_at
                ? `Expired ${formatDate(data.expires_at)}. `
                : ""}
              Nothing is auto-cancelled — contact us and we'll sort it out.
            </span>
          ) : null}
          {state === "unknown" ? (
            <span className="type-bodyS maisight-commerce-status-expiry">
              We can't confirm a license for this workspace right now.
            </span>
          ) : null}
        </div>
      </Card>

      <Card header="THIS PERIOD">
        <div className="maisight-commerce-usage">
          {data.plan ? (
            <div>
              <div className="maisight-commerce-key-value">
                <span className="type-numSm maisight-commerce-key-value-label">
                  Domains
                </span>
                <span className="type-numSm maisight-commerce-key-value-value">
                  {data.domains.length} of {domainLimit ?? "unlimited"}
                </span>
              </div>
              {domainLimit !== null ? (
                <MeterBar
                  value={data.domains.length}
                  max={domainLimit}
                  tone={data.domains.length >= domainLimit ? "flagged" : "nominal"}
                  label={`${data.domains.length} of ${domainLimit} domains bound`}
                />
              ) : null}
            </div>
          ) : null}
          {usageData ? (
            <StatRow>
              <StatTile
                label="Documents with activity"
                value={usageData.document_count}
                detail={`Last ${usageData.window_days} days`}
              />
              <StatTile
                label="Terms with activity"
                value={usageData.term_count}
                detail={`Last ${usageData.window_days} days`}
              />
              <StatTile
                label="Extraction cost"
                value={
                  usageData.cost_unknown
                    ? "unknown"
                    : formatUsd(usageData.total_cost_usd)
                }
                detail={
                  usageData.cost_unknown
                    ? "Pricing data unavailable"
                    : `Last ${usageData.window_days} days`
                }
              />
            </StatRow>
          ) : (
            <p className="type-bodyS maisight-commerce-status-expiry">
              Usage figures are unavailable right now.
            </p>
          )}
        </div>
      </Card>

      <div className="maisight-commerce-plan-row">
        {UPGRADE_TIERS.map((tier) => (
          <PlanCard
            key={tier}
            tier={tier}
            isCurrent={data.plan === tier}
            supportHref={to.support}
            onInert={setInertMessage}
          />
        ))}
      </div>

      <p className="type-bodyS maisight-commerce-disclaimer">
        Self-serve checkout isn't live yet — plan changes go through us for
        now, and nothing on this page collects or stores payment details.
      </p>

      {inertMessage ? (
        <Toast
          title="Checkout isn't available yet"
          description={inertMessage}
          tone="accent"
          onDismiss={() => setInertMessage(null)}
        />
      ) : null}
    </div>
  );
}
